import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Plus, UserCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = API_BASE_URL;

const roleLabels = {
  super_admin: 'Super Admin',
  company_admin: 'Restaurant Owner',
  restaurant_manager: 'Manager',
  supervisor: 'Supervisor',
  staff: 'Kitchen Staff'
};

const roleColors = {
  super_admin: '#232D4B',
  company_admin: '#E57200',
  restaurant_manager: '#3B82F6',
  supervisor: '#6366F1',
  staff: '#10B981'
};

const Users = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', company_id: '', restaurant_id: '' });

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, restaurantsRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/restaurants`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setRestaurants(restaurantsRes.data);
    } catch (error) {
      console.error(error);
    }

    if (user?.role === 'super_admin') {
      try {
        const companiesRes = await axios.get(`${API}/companies`, { withCredentials: true });
        setCompanies(companiesRes.data);
      } catch (error) {
        console.error(error);
        setCompanies([]);
      }
    } else {
      setCompanies([]);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async () => {
    try {
      const payload = {
        ...form,
        company_id: form.company_id || user?.company_id || null,
        restaurant_id: form.restaurant_id || null
      };
      await axios.post(`${API}/users`, payload, { withCredentials: true });
      toast.success('User created');
      setOpenAdd(false);
      setForm({ name: '', email: '', password: '', role: 'staff', company_id: '', restaurant_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const getRestaurantName = (id) => restaurants.find(r => r.id === id)?.name || '-';
  const getCompanyName = (id) => companies.find(c => c.id === id)?.name || 'Unknown company';

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
          {t('users')}
        </h1>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: '#E57200' }} data-testid="add-user-btn">
              <Plus className="h-4 w-4 me-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {user?.role === 'super_admin' && <SelectItem value="company_admin">Restaurant Owner</SelectItem>}
                    <SelectItem value="restaurant_manager">Manager</SelectItem>
                    <SelectItem value="staff">Kitchen Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {user?.role === 'super_admin' && form.role !== 'super_admin' && (
                <div>
                  <Label>Company</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>
                      {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {(form.role === 'restaurant_manager' || form.role === 'staff') && (
                <div>
                  <Label>Assigned Restaurant</Label>
                  <Select value={form.restaurant_id} onValueChange={(v) => setForm({ ...form, restaurant_id: v })}>
                    <SelectTrigger data-testid="user-restaurant"><SelectValue placeholder="Select restaurant" /></SelectTrigger>
                    <SelectContent>
                      {restaurants.length === 0 ? (
                        <SelectItem value="none" disabled>No restaurants available</SelectItem>
                      ) : (
                        restaurants.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleAdd} style={{ backgroundColor: '#E57200' }} data-testid="save-user-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border/40">
        <CardContent className="pt-6 overflow-x-auto">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Restaurant</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No users yet</TableCell></TableRow>
              ) : (
                users.map(u => (
                  <TableRow key={u.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium flex items-center gap-2">
                      <UserCircle className="h-4 w-4" style={{ color: '#E57200' }} />
                      {u.name}
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge style={{ backgroundColor: roleColors[u.role] || '#64748B', color: 'white' }}>
                        <span>{roleLabels[u.role] || 'Unknown role'}</span>
                        <span className="block text-xs font-normal">({u.role})</span>
                      </Badge>
                    </TableCell>
                    <TableCell>{u.company_id ? getCompanyName(u.company_id) : '-'}</TableCell>
                    <TableCell>{u.restaurant_id ? getRestaurantName(u.restaurant_id) : '-'}</TableCell>
                    <TableCell>
                      {u.id !== user?.id && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Users;
